<template>
  <div class="search-table">
    <el-card v-if="showSearch">
      <el-form ref="formRef" :inline="true" :model="searchParams" v-bind="getFormBindVlaue">
        <el-row :gutter="20">
          <el-col
            :span="search.span"
            :key="column.prop"
            v-for="column in visibleSearchColumns.slice(0, spanCount - 1)"
          >
            <el-form-item :label="`${column.label} :`" :prop="column.prop">
              <component
                :is="column.type"
                v-model="searchParams[column.prop]"
                v-bind="column.search"
                @keyup.enter="handleEnter(column)"
              >
                <template #default>
                  <template v-if="column.type === 'el-select'">
                    <el-option v-for="item in column.search.options" :label="item.label" :value="item.value" :key="item.value" />
                  </template>
                </template>
              </component>
            </el-form-item>
          </el-col>
          <el-col :span="search.span">
            <el-form-item class="search-btn-group">
              <el-button type="primary" @click="submitForm">查询</el-button>
              <el-button @click="resetForm">重置</el-button>
              <el-button v-if="showAdvanceBtn" type="text" :icon="isAdvanced ? ArrowUp : ArrowDown" @click="toggleAdvanced">
                {{ isAdvanced ? '收起' : '展开' }}
              </el-button>
            </el-form-item>
          </el-col>
          <el-col
            :span="search.span"
            :key="column.prop"
            v-for="column in visibleSearchColumns.slice(spanCount - 1)"
          >
            <el-form-item :label="`${column.label} :`" :prop="column.prop">
              <component
                :is="column.type"
                v-model="searchParams[column.prop]"
                v-bind="column.search"
              >
                <template #default>
                  <template v-if="column.type === 'el-select'">
                    <el-option v-for="item in column.search.options" :label="item.label" :value="item.value" :key="item.value" />
                  </template>
                </template>
              </component>
            </el-form-item>
          </el-col>
        </el-row>
      </el-form>
    </el-card>
    <el-card class="table-wrapper" v-loading="isLoading">
      <el-table :data="tableData" :max-height="tableHeight" :show-overflow-tooltip="true" v-bind="getBindValues" @sort-change="handleTableSort">
        <template #default>
          <el-table-column
            v-for="column in columns"
            :key="column.prop"
            v-bind="getBingColumnValues(column)"
          >
            <template #default="scope">
              <slot v-if="column.slot" :name="column.prop" :scope="scope" />
              <template v-else>
                {{ column.formatter ? column.formatter(scope.row, scope.column, scope.row[column.prop], scope.$index) : (scope.row[column.prop] || '-') }}
              </template>
            </template>
          </el-table-column>
        </template>
        <template #append>
          <span v-if="showCount && tableCount" class="table-total">共 {{tableCount}} 条数据</span>
        </template>
      </el-table>
      <el-pagination
        v-if="tableCount"
        :total="tableCount"
        :current-page="pageParams.page"
        :page-size="pageParams.limit"
        :page-sizes="[10, 20, 50]"
        @current-change="(v) => handlePageChange(v, 'page')"
        @size-change="(v) => handlePageChange(v, 'limit')"
        layout="slot, prev, pager, next, sizes"
      >
        <template #default>
          <span class="el-pagination__total is-first">
            共 {{ tableCount }} 条
          </span>
        </template>
      </el-pagination>
    </el-card>
  </div>
</template>
<script setup>
import { ref, unref, computed, reactive, onMounted } from "vue";
import { ArrowUp, ArrowDown } from '@element-plus/icons-vue';

const props = defineProps({
  api: {
    type: Function,
    default: () => {}
  },
  showSearch: {
    type: Boolean,
    default: true,
  },
  showCount: {
    type: Boolean,
    default: false,
  },
  search: {
    type: Object,
    default: () => ({
      span: 6,
      labelWidth: 100,
    })
  },
  columns: {
    type: Array,
    required: true
  },
  data: {
    type: Array,
    required: true
  },
  rowKey: {
    type: String,
    default: 'id'
  },
  size: {
    type: String,
    default: 'default'
  },
  border: {
    type: Boolean,
    default: false
  },
  total: {
    type: Number,
    default: 0
  },
  getRows: {
    type: Function,
    default: (d) => d
  },
  defaultSort: {
    type: Object,
    default: () => ({})
  }
});

let tableData = reactive(props.data);
const formRef = ref();
const tableSize = ref(props.size);
const tableCount = ref(props.total)
const searchParams = reactive({});
const sortParams = reactive({ column: props.defaultSort?.prop, order: props.defaultSort?.order?.replace('ending', '') })
const pageParams = reactive({ page: 1, limit: 10 })
const isAdvanced = ref(false);
const isLoading = ref(true)

// 获取表格属性
const getBindValues = computed(() => {
  return {
    rowKey: props.rowKey,
    size: unref(tableSize),
    border: props.border,
    'default-sort': props.defaultSort
  }
});

// 获取表单属性
const getFormBindVlaue = computed(() => {
  return {
    labelWidth: props.search.labelWidth,
  }
});

const tableHeight = computed(() => {
  return (window.innerHeight - 60) - (window.innerWidth * 0.06) - 140 - (isAdvanced.value ? 140 : 95)
})
// 获取表单项属性
const searchColumns = computed(() => {
  const typeMap = {
    'input': 'el-input',
    'select': 'el-select',
    'date': 'el-date-picker',
    'radio': 'el-radio-group',
  };  

  const defaultProps = {
    'input': {
      clearable: true,
      placeholder: '请输入',
    },
    'select': {
      clearable: true,
      placeholder: '请选择',
      options: []
    },
  }
  return props.columns.filter(column => !column.search?.hide).map(column => {
    const type = column?.type || 'input'
    return {
      ...column,
      type: typeMap[type],
      search: {
        ...defaultProps[type],
        ...column.search
      }
    }
  });
});

// 展开收起控制展示的表单项
const visibleSearchColumns = computed(() => {
  return searchColumns.value.filter((_, index) => isAdvanced.value || index + 1 < spanCount.value)
})

// 获取表单是否需要展示
const showAdvanceBtn = computed(() => {
  return (searchColumns.value.length + 1) * props.search.span > 24;
});

// 获取表单一行展示个数
const spanCount = computed(() => 24 / props.search.span)

const getBingColumnValues = (column) => {
  const { type, ...others } = column;
  let width = column.width;
  if (!width) {
    width = type === 'index' ? '80px' : '';
  }

  return {
    'sort-orders': ['ascending', 'descending'],
    ...others,
    type,
    width
  }
};

const getParams = () => {
  let params = {
    pageNo: pageParams.page,
    pageSize: pageParams.limit,
    ...sortParams,
    ...searchParams,
  }
  Object.keys(params).forEach(key => {
    const column = props.columns.find(v => v.prop === key)
    const value = params[key]
    if (value && column?.search?.transform) {
      delete params[key]
      params = { ...params, ...column.search?.transform(value)}
    } else {
      params[key] = params[key] ? encodeURIComponent(params[key]) : ''
    }
  })
  return params
}

const handleSearch = (resetPage) => {
  if (resetPage) {
    pageParams.page = 1
  }
  isLoading.value = true
  props.api(getParams()).then(res => {
    if (res) {
      tableData = props.getRows(res.records)
      tableCount.value = res.total
    }
    isLoading.value = false
  })
}

const handleTableSort = (sort) => {
  sortParams.column = sort.prop
  sortParams.order = sort.order?.replace('ending', '')
  console.log(sort)
  handleSearch()
}
const resetForm = () => {
  if (!formRef.value) return
  formRef.value.resetFields()
  handleSearch(true)
}

const submitForm = () => {
  if (!formRef.value) return
  formRef.value.validate((valid) => {
    if (valid) {
      handleSearch(true)
    }
  })
}

const handleEnter = (column) => {
  if (column.type === 'el-input') {
    submitForm()
  }
}

const handlePageChange = (val, key) => {
  if (key === 'limit') {
    pageParams.page = 1
  }
  pageParams[key] = val
  handleSearch()
}

const handleDelete = (deleteCount = 1) => {
  if ((tableCount.value - deleteCount) % pageParams.limit === 0) {
    console.log(pageParams, 'pagedelete')
    pageParams.page = Math.max(pageParams.page - 1, 1)
  }
  handleSearch(false)
}

const toggleAdvanced = () => {
  isAdvanced.value = !isAdvanced.value
}

onMounted(() => {
  handleSearch()
})

defineExpose({
  reload: () => handleSearch(true),
  refresh: () => handleSearch(false),
  delete: handleDelete,
  getParams,
})
</script>

<style lang="scss" scoped>
.search-table {
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 20px;
  :deep(.el-card) {
    box-shadow: none;
    border-radius: 8px;
    .el-card__body {
      padding: 30px 20px;
    }
  }
  :deep(.el-form) {
    .el-row {
      row-gap: 16px;
      justify-content: flex-start;
    }
    .el-form-item {
      width: 100%;
      margin-right: 0;
      margin-bottom: 0;
    }
  }
  :deep(.el-pagination) {
    margin-top: 16px;
    justify-content: flex-end;
    .btn-prev,
    .el-pagination__sizes {
      margin-left: 8px;
    }
  }
  .search-btn-group {
    :deep(.el-form-item__content) {
      justify-content: flex-end;
      .el-button {
        min-width: 80px;
      }
    }
  }
  .table-wrapper {
    flex: 1;
    :deep(.el-card__body) {
      height: 100%;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
    }
    :deep(.el-table--default .el-table__cell) {
      padding: 12px 0;
    }
    :deep(.el-table th.el-table__cell) {
      font-weight: 500;
      background: #DEDEDE;
    }
  }
  .table-total {
    display: block;
    padding: 10px 30px;
  }
}
</style>